/**
 * CtaGradient — the green gradient fill for primary CTAs.
 *
 * RAMP: BUTTON_GRADIENT — DARK AT THE TOP, light at the bottom, vertical.
 * Founder 2026-08-02: "a dark to light may be better vs the sheen…
 * especially for buttons that are under a darker card, they flow better."
 * The dark end IS GLASS_ROOTS, the exact colour of the dark cards and drawer
 * headers, so a button beneath one continues that surface instead of starting
 * a new note under it.
 *
 * ⚠️ DO NOT ALSO MOUNT InstrumentSheen ON A CTA. The ramp is the light now;
 * a sheen on top puts two light sources on one small surface and reads as a
 * smudge. (Cards are different — they are big enough to carry a sheen over a
 * flat fill, which is what the dark instruments do.)
 *
 * WHY THIS EXISTS: the founder liked the Next Week reveal hero's gradient and
 * asked for the same on the subscription CTA family (2026-08-02: "can we do
 * this too for the Manage Subscription button ... same for start trial etc").
 * That family is THREE separate buttons in three files, each with its own copy
 * of `backgroundColor: cedarPrime`. Painting a gradient into each by hand is
 * how the nine-divergent-segmented-strips problem started, so the material
 * lives here once and adoption is a one-line child.
 *
 * USAGE — drop in as the FIRST child of a button that clips:
 *
 *   button: { ...,  overflow: 'hidden' }        // required, see GEOMETRY
 *
 *   <Pressable style={styles.button}>
 *     <CtaGradient />
 *     <InstrumentSheen />   // optional, sits on top of the fill
 *     <Text style={styles.label}>Manage Subscription</Text>
 *   </Pressable>
 *
 * The gradient is absolutely positioned, so it paints BEHIND the button's own
 * children while covering its `backgroundColor` — which is exactly why the
 * press state has to change (see PRESS below).
 *
 * GEOMETRY: identical over-bleed to InstrumentSheen, for the identical reason.
 * Yoga insets absolute children by the parent's padding, so a plain
 * `StyleSheet.absoluteFill` inside a padded button leaves an unpainted band at
 * every edge (these buttons pad 14–16pt). The wrapper over-bleeds a fixed 24pt
 * in every direction and the button's `overflow: 'hidden'` clips it back — so
 * the parent MUST clip or the fill spills past the button's corners.
 *
 * PRESS: an opaque gradient paints over `backgroundColor`, so the old
 * `pressed && { backgroundColor: thrive }` is invisible underneath it and the
 * button silently stops acknowledging taps. Gradient surfaces press by lighting
 * their EDGE (CTA_GRADIENT_PRESSED === PRESSED_THRIVE: a thrive border plus a
 * 0.985 scale — it moves no ink and no fill). That also retires a real defect:
 * white on thrive measures 1.90:1, so a thrive press fill put the label below
 * the AA floor for as long as it was held.
 *
 * ⚠️ The edge only lands because AnimatedPressable injects its resting
 * GLASS_EDGE as a DEFAULT (composed before the caller's style), not as an
 * override. Composed after, it silently overwrote every caller's pressed
 * borderColor — which disabled this press at 13 of 14 sites until 2026-08-16.
 *
 * CONTRAST: both gradient stops clear 4.5:1 against white text (cedarPrime
 * 6.33:1, CEDAR_BRIGHT 4.56:1), so a white label is legible at every point
 * along the ramp — not just at the ends. Pinned by TestCtaGradientIsAccessible.
 *
 * TWO BUTTONS, ONE COMPONENT (dark-mode sprint, P2c).
 *   <CtaGradient />          the BRAND ramp, the same in both themes. A button
 *                            that mounts this is green with a white label in
 *                            light and in dark. Every screen that has not been
 *                            moved to the theme mounts this one, so its white
 *                            label stays readable when the room goes dark.
 *   <CtaGradient themed />   the THEME's button (`cta.gradient`): the same ramp
 *                            in light; in dark, the flat thrive fill, which
 *                            takes `ink.onCta` (Cedar ink), not white.
 * A screen switches to `themed` when it is moved to the theme, in the same
 * change that moves its label to `ink.onCta`. Never one without the other.
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../theme';

/** Must exceed the largest CTA padding (16pt) so Yoga's inset on absolute
 *  children can never pull the fill off the button's edges. */
const BLEED = 24;

export function CtaGradient({ themed = false }: { themed?: boolean }) {
  const { palette } = useTheme();
  const gradient = themed ? palette.cta.gradient : palette.glass.buttonGradient;
  return (
    <View pointerEvents="none" style={styles.bleedWrap}>
      <LinearGradient
        colors={[...gradient.colors]}
        start={gradient.start}
        end={gradient.end}
        style={StyleSheet.absoluteFill}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  bleedWrap: {
    position: 'absolute',
    top: -BLEED,
    left: -BLEED,
    right: -BLEED,
    bottom: -BLEED,
  },
});

export default CtaGradient;
